import { ReactNode } from 'react';

export const gostHashContent: {
    title: string;
    description: string;
    content: ReactNode;
    faqs: readonly { question: string; answer: string }[];
    relatedTools: readonly { name: string; url: string; description: string }[];
} = {
    title: "GOST Hash Generator",
    description: "Generate GOST cryptographic hash values for data security and integrity. Free online GOST hash calculator.",

    content: (
        <div className="prose max-w-none text-gray-700">
            <p>
                GOST is a family of cryptographic hash functions defined in Russian standards (GOST R 34.11-94 and GOST R 34.11-2012). These are secure hash functions used in Russian cryptographic systems.
            </p>

            <h3>What is GOST Hash?</h3>
            <ul>
                <li><strong>Cryptographically secure</strong> hash algorithms</li>
                <li><strong>Russian national standards</strong> for digital signatures</li>
                <li><strong>256-bit or 512-bit</strong> output sizes</li>
                <li><strong>Government-approved</strong> for official use</li>
            </ul>

            <h3>GOST Hash Variants</h3>
            <h4>GOST R 34.11-94</h4>
            <ul>
                <li>Output: 256-bit hash</li>
                <li>Block Size: 64 bytes</li>
                <li>Status: Legacy standard</li>
                <li>Security: Considered broken</li>
            </ul>
            <h4>GOST R 34.11-2012</h4>
            <ul>
                <li>GOST-256: 256-bit hash</li>
                <li>GOST-512: 512-bit hash</li>
                <li>Block Size: 64 bytes</li>
                <li>Status: Current standard</li>
                <li>Security: Considered secure</li>
            </ul>

            <h3>How GOST Hash Works</h3>
            <ol>
                <li>Initialization: Set initial hash value</li>
                <li>Padding: Add padding to message</li>
                <li>Compression: Process message blocks</li>
                <li>Transformation: Apply GOST transformation</li>
                <li>Output: Final hash value</li>
            </ol>

            <h3>Use Cases</h3>
            <ul>
                <li>Digital Signatures (Russian government systems)</li>
                <li>Document Authentication</li>
                <li>Password Hashing</li>
                <li>Data Integrity Checks</li>
                <li>Cryptographic Protocols</li>
            </ul>

            <h3>Advantages</h3>
            <ul>
                <li>✅ Cryptographically secure</li>
                <li>✅ Government standard approval</li>
                <li>✅ Multiple output sizes</li>
                <li>✅ Well-specified algorithm</li>
                <li>✅ Extensively analyzed</li>
                <li>✅ No known practical attacks</li>
            </ul>

            <h3>Limitations</h3>
            <ul>
                <li>❌ Slower than modern hashes</li>
                <li>❌ Limited adoption outside Russia</li>
                <li>❌ Complex implementation</li>
                <li>❌ Legacy version vulnerabilities</li>
                <li>❌ Limited library support</li>
            </ul>
        </div>
    ),

    faqs: [
        {
            question: "What is GOST hash used for?",
            answer: "GOST hash is primarily used in Russian government systems, digital signatures, document authentication, and applications requiring compliance with Russian cryptographic standards."
        },
        {
            question: "Is GOST hash secure?",
            answer: "GOST R 34.11-2012 is considered secure, while the legacy GOST R 34.11-94 has known vulnerabilities. Always use the 2012 version for security-critical applications."
        },
        {
            question: "What's the difference between GOST-256 and GOST-512?",
            answer: "GOST-256 produces a 256-bit hash while GOST-512 produces a 512-bit hash. GOST-512 provides higher security but is slower and produces larger outputs."
        },
        {
            question: "Can I use GOST hash for passwords?",
            answer: "Yes, GOST-256 or GOST-512 can be used for password hashing, but consider using specialized password hashing functions like Argon2 or bcrypt for better security."
        },
        {
            question: "Is GOST hash compatible with SHA-256?",
            answer: "No, GOST and SHA-256 are different algorithms and produce different hash values for the same input. They are not interchangeable."
        },
        {
            question: "Where is GOST hash legally required?",
            answer: "GOST hash is legally required in Russian government systems, banking applications, and any systems that must comply with Russian cryptographic regulations."
        }
    ],

    relatedTools: [
        { name: "SHA-256 Hash Generator", url: "/tools/sha256-hash", description: "Generate SHA-256 hash for modern security" },
        { name: "Hash Generator", url: "/tools/hash-generator", description: "Generate various hash values for comprehensive security" },
        { name: "AES Encryption Tool", url: "/tools/aes-encrypt", description: "Encrypt and decrypt data using AES encryption" },
        { name: "Bcrypt Password Generator", url: "/tools/bcrypt-generator", description: "Generate secure bcrypt hashes for password storage" },
        { name: "Base64 Encoder", url: "/tools/base64", description: "Encode and decode Base64 strings for data transmission" }
    ],
};